import { useCallback, useEffect, useState } from 'react';

interface UseLocalStorageOptions {
  key: string;
  returnNull?: boolean;
}

/**
 * A hook that always returns the latest value from localStorage.
 * This is useful when you need to ensure you have the most up-to-date value,
 * especially when the value might be changed from other tabs or windows.
 * @param options - Configuration options for the hook
 * @param options.key - The key to look up in localStorage
 * @param options.returnNull - If true, returns null when key doesn't exist. If false, returns false.
 * @returns The current value from localStorage
 */
export const useLocalStorage = <T>({
  key,
  returnNull = true,
}: UseLocalStorageOptions): T | null | false => {
  const getValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return returnNull ? null : false;
    }
    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        return returnNull ? null : false;
      }
      return JSON.parse(item);
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return returnNull ? null : false;
    }
  }, [key, returnNull]);

  // State to store the current value
  const [value, setValue] = useState<T | null | false>(getValue);

  // Update the value whenever localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setValue(getValue());
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Also listen for changes in the current tab
    const interval = setInterval(handleStorageChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [getValue]);

  return value;
};
